import * as React from "react";
import { isTtnApproved } from "~/shared/lib/shift-status";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";
import { getRouteFactVolume } from "../../utils/routeMetrics";
import { isFinalJobStatus } from "~/shared/lib/job-status";
import {
  closeTo,
  EMPTY_DRAFT,
  ENGINE_HOURS_EPSILON,
  firstPlanned,
  getRouteScaleWeight,
  isRealRoute,
  isShownOnTtn,
  isTimeRangeInverted,
  lastPlanned,
  mergePatch,
  MILEAGE_EPSILON,
  optionsOf,
  pick,
  sameId,
  sameTime,
  savedAgreement,
  ts,
  WEIGHT_EPSILON,
  type Agreement,
  type Draft,
  type FieldSource,
  type RoutePatch,
  type RoutePatchKey,
  type ShiftPatch,
  type ShiftPatchKey,
  type SourceOption,
} from "./ttnDraftUtils";
import { scaleTimeOf, type GpsEvent } from "./ttnGps";
import { useTtnGps, useTtnTrack } from "./useTtnTelemetry";
import { useTtnWrites } from "./useTtnWrites";

/**
 * TTN form state. Shift, crew and trip inputs edit a local draft (kept per
 * shift) that `save` writes on "approve". The returned `shift` / `routes` have
 * the draft and the fallbacks for still-empty fields applied, so what is on
 * screen is exactly what `save` writes — the backend stamps
 * `consignmentNoteDoneAt` once it's complete.
 */
/** A trip's start / unload as shown, and where each came from. */
export type TripTimes = {
  start?: Date;
  end?: Date;
  startSource?: FieldSource;
  endSource?: FieldSource;
  /** Set once approved: whether the stored time still agrees with GPS / scale / plan. */
  startAgrees?: Agreement;
  endAgrees?: Agreement;
  /** The GPS unload place, when it differs from the route's own. */
  derivedPlace?: GpsEvent;
};

type ShiftField =
  | "departureTime"
  | "departureLocationId"
  | "arrivalTime"
  | "arrivalLocationId"
  | "engineHours"
  | "mileage";
type RouteField = "start" | "end" | "place" | "weight" | "mileage";

export type ShiftFieldSources = Partial<Record<ShiftField, FieldSource>>;
export type RouteFieldSources = Partial<Record<RouteField, FieldSource>>;

/** Set only for fields showing the approved value — it colours their borders. */
export type ShiftFieldAgreement = Partial<Record<ShiftField, Agreement>>;
export type RouteFieldAgreement = Partial<Record<RouteField, Agreement>>;

/** What each field can be set to off its right-click menu, source by source. */
export type ShiftFieldOptions = Partial<Record<ShiftField, SourceOption[]>>;
export type RouteFieldOptions = Partial<Record<RouteField, SourceOption[]>>;

export const useTtnDraft = (
  shift: ConnectProto.DriverOnShift | undefined,
  routes: ConnectProto.Route[],
  crewCandidates: ConnectProto.User[],
) => {
  const [drafts, setDrafts] = React.useState(new Map<bigint, Draft>());
  const draft = (shift && drafts.get(shift.id)) || EMPTY_DRAFT;

  const update = (recipe: (current: Draft) => Draft) => {
    if (!shift) return;
    setDrafts((prev) => new Map(prev).set(shift.id, recipe(prev.get(shift.id) ?? EMPTY_DRAFT)));
  };
  const setShift = (patch: ShiftPatch) =>
    update((d) => ({ ...d, shift: mergePatch(d.shift, patch) }));
  const setCrew = (crewIds: bigint[]) => update((d) => ({ ...d, crewIds }));
  // An emptied trip patch goes, or the draft would still count as changed.
  const withRoute = (d: Draft, routeId: bigint, next: RoutePatch) => {
    const routes = new Map(d.routes);
    if (Object.keys(next).length > 0) routes.set(routeId, next);
    else routes.delete(routeId);
    return { ...d, routes };
  };
  const setRoute = (route: ConnectProto.Route, patch: RoutePatch) =>
    update((d) => withRoute(d, route.id, mergePatch(d.routes.get(route.id) ?? {}, patch)));

  // Once the TTN is approved, what was approved stays: GPS only fills a TTN
  // that is still open. A manual edit wins either way.
  const isDone = isTtnApproved(shift);
  const gps = useTtnGps(shift, routes);

  // Trip start / unload (design 16456:40726): the edit, else (for an open TTN)
  // the GPS exit from the garage or disposal site / the scale ticket, else the
  // GPS entry at the disposal site, else the trip's actual times, else the plan.
  const tripTimes = React.useMemo(() => {
    const agrees = savedAgreement(isDone);
    const byRoute = new Map<bigint, TripTimes>();
    for (const route of routes) {
      const patch = draft.routes.get(route.id) ?? {};
      // The GPS as it reads now — what an approved time is checked against, and
      // what fills an open TTN.
      const rawGps = gps.tripsGps.get(route.id);
      const tripGps = isDone ? undefined : rawGps;
      const derivedStart = patch.factStartAt ? undefined : tripGps?.start;
      const derivedEnd =
        patch.factEndAt || isDone ? undefined : (scaleTimeOf(route) ?? tripGps?.unload?.at);
      const gpsPlace = patch.destination ? undefined : tripGps?.unload;
      // An approved TTN saved its trip times as the route's actual ones.
      const actual: FieldSource = isDone ? "saved" : "driver";
      const start = pick(
        [patch.factStartAt, patch.from?.factStartAt ?? "manual"],
        [derivedStart, "gps"],
        [fromTimestamp(route.factStartAt), actual],
        [fromTimestamp(route.planStartAt), "plan"],
      );
      const scaleTime = patch.factEndAt || isDone ? undefined : scaleTimeOf(route);
      const end = pick(
        [patch.factEndAt, patch.from?.factEndAt ?? "manual"],
        [scaleTime, "scale"],
        [derivedEnd, "gps"],
        [fromTimestamp(route.factEndAt), actual],
        [fromTimestamp(route.planEndAt), "plan"],
      );
      // Approval writes the trip times into the route's own `fact*`, so the
      // driver's marks are no longer a separate value to compare with: a time
      // that matches neither GPS, the scale nor the plan is reported as
      // agreeing with nothing, whoever put it there.
      byRoute.set(route.id, {
        start: start.value,
        end: end.value,
        startSource: start.source,
        endSource: end.source,
        startAgrees: agrees(
          start,
          sameTime,
          [rawGps?.start, "gps"],
          [fromTimestamp(route.planStartAt), "plan"],
        ),
        endAgrees: agrees(
          end,
          sameTime,
          [scaleTimeOf(route), "scale"],
          [rawGps?.unload?.at, "gps"],
          [fromTimestamp(route.planEndAt), "plan"],
        ),
        derivedPlace:
          gpsPlace && gpsPlace.locationId !== route.destinationLocationId ? gpsPlace : undefined,
      });
    }
    return byRoute;
  }, [routes, draft, gps.tripsGps, isDone]);

  // Выезд / Возвращение: the edit, else (for an open TTN) the first garage exit
  // / last garage entry, else the saved TTN value, else the shift's actual start
  // / end, else the trips' plan.
  const shiftTimes = React.useMemo(() => {
    if (!shift) return undefined;
    const agrees = savedAgreement(isDone);
    const patch = draft.shift;
    // A cancelled trip's plan is not the shift's.
    const planned = routes.filter(isShownOnTtn);
    const first = firstPlanned(planned);
    const last = lastPlanned(planned);
    const departure = isDone ? undefined : gps.departure;
    const arrival = isDone ? undefined : gps.arrival;
    // A route's destination is its unload site, not where the truck goes home,
    // so the return place falls back to the depot the shift left from.
    const manual = (key: ShiftPatchKey) => patch.from?.[key] ?? "manual";
    const departurePlace = pick(
      [patch.departureLocationId, manual("departureLocationId")],
      [departure?.locationId, "gps"],
      [shift.departureLocationId || undefined, "saved"],
      [first?.sourceLocationId || undefined, "plan"],
    );
    const arrivalPlace = pick(
      [patch.arrivalLocationId, manual("arrivalLocationId")],
      [arrival?.locationId, "gps"],
      [shift.arrivalLocationId || undefined, "saved"],
      [departurePlace.value, "plan"],
    );
    const departureTime = pick(
      [patch.departureTime, manual("departureTime")],
      [departure?.at, "gps"],
      [fromTimestamp(shift.departureTime), "saved"],
      [fromTimestamp(shift.startedAt), "driver"],
      [fromTimestamp(first?.planStartAt), "plan"],
    );
    const arrivalTime = pick(
      [patch.arrivalTime, manual("arrivalTime")],
      [arrival?.at, "gps"],
      [fromTimestamp(shift.arrivalTime), "saved"],
      [fromTimestamp(shift.completedAt), "driver"],
      [fromTimestamp(last?.planEndAt), "plan"],
    );
    return {
      departureLocationId: departurePlace.value,
      arrivalLocationId: arrivalPlace.value,
      departureTime: departureTime.value,
      arrivalTime: arrivalTime.value,
      sources: {
        departureLocationId: departurePlace.source,
        arrivalLocationId: arrivalPlace.source,
        departureTime: departureTime.source,
        arrivalTime: arrivalTime.source,
      },
      // The driver's own marks stay a separate field here (`startedAt` /
      // `completedAt`), so an approved time can still be told apart from them.
      agreement: {
        departureLocationId: agrees(
          departurePlace,
          sameId,
          [gps.departure?.locationId, "gps"],
          [first?.sourceLocationId || undefined, "plan"],
        ),
        arrivalLocationId: agrees(
          arrivalPlace,
          sameId,
          [gps.arrival?.locationId, "gps"],
          // Same fallback as the shown value: home is where the shift left from.
          [departurePlace.value, "plan"],
        ),
        departureTime: agrees(
          departureTime,
          sameTime,
          [gps.departure?.at, "gps"],
          [fromTimestamp(shift.startedAt), "driver"],
          [fromTimestamp(first?.planStartAt), "plan"],
        ),
        arrivalTime: agrees(
          arrivalTime,
          sameTime,
          [gps.arrival?.at, "gps"],
          [fromTimestamp(shift.completedAt), "driver"],
          [fromTimestamp(last?.planEndAt), "plan"],
        ),
      } satisfies ShiftFieldAgreement,
      // Raw, the TTN approved or not: the menu offers what each source says today.
      options: {
        departureLocationId: optionsOf(
          [gps.departure?.locationId, "gps"],
          [first?.sourceLocationId || undefined, "plan"],
        ),
        arrivalLocationId: optionsOf(
          [gps.arrival?.locationId, "gps"],
          [departurePlace.value, "plan"],
        ),
        departureTime: optionsOf(
          [gps.departure?.at, "gps"],
          [fromTimestamp(shift.startedAt), "driver"],
          [fromTimestamp(first?.planStartAt), "plan"],
        ),
        arrivalTime: optionsOf(
          [gps.arrival?.at, "gps"],
          [fromTimestamp(shift.completedAt), "driver"],
          [fromTimestamp(last?.planEndAt), "plan"],
        ),
      } satisfies ShiftFieldOptions,
    };
  }, [shift, routes, draft, gps.departure, gps.arrival, isDone]);

  // The shift's window, Выезд → Возвращение as shown, else its planned window.
  // Never "now": an unclosed shift would then stretch to today. It bounds the
  // track (engine hours, mileage, playback) and the engine-hours request.
  const shiftSpan = React.useMemo(() => {
    if (!shift || !shiftTimes) return null;
    const from =
      shiftTimes.departureTime ?? fromTimestamp(shift.twStartAt) ?? fromTimestamp(shift.createdAt);
    const to = shiftTimes.arrivalTime ?? fromTimestamp(shift.twEndAt);
    return from && to && to > from ? { from, to } : null;
  }, [shift, shiftTimes]);

  const tripSpans = React.useMemo(
    () =>
      new Map(
        routes
          .filter((route) => route.status !== ConnectProto.RouteStatus.CANCELLED)
          .map((route) => {
            const times = tripTimes.get(route.id);
            return [route.id, { from: times?.start, to: times?.end }] as const;
          }),
      ),
    [routes, tripTimes],
  );

  const { trackStats, mileageByRoute, returnMileage, sensorEngineHours } = useTtnTrack(
    shift,
    shiftSpan,
    tripSpans,
    gps.stopsWindow,
  );

  // Unload place: the edit, else (for an open TTN) the GPS disposal visit, else
  // the route's own. Trip times stay the stored actual ones here — the jobs tab
  // and the playback read them as such; the shown ones are `tripTimes`. Volume
  // is always derived; weight is the scale ticket's when there is one, else the
  // declared value. Mileage: the edit, else (for an open TTN) the track, else
  // the saved value — the track once more if an approved TTN saved none.
  const { routesView, routeSources, routeAgreement, routeOptions } = React.useMemo(() => {
    const agrees = savedAgreement(isDone);
    const sources = new Map<bigint, RouteFieldSources>();
    const agreement = new Map<bigint, RouteFieldAgreement>();
    const options = new Map<bigint, RouteFieldOptions>();
    const view = routes.map((route) => {
      const patch = draft.routes.get(route.id) ?? {};
      const manual = (key: RoutePatchKey) => patch.from?.[key] ?? "manual";
      const rawGps = gps.tripsGps.get(route.id);
      const rawUnload = rawGps?.unload;
      const gpsUnload = isDone ? undefined : rawUnload;
      const trackMileage = mileageByRoute.get(route.id);
      const place = pick(
        [patch.destination?.id, manual("destination")],
        [gpsUnload?.locationId, "gps"],
        [route.destinationLocationId || undefined, isDone ? "saved" : "plan"],
      );
      // Zero included: a declared zero wins over an empty field. An edit wins
      // over the scale ticket too. Once approved, the approved weight stays even
      // if a scale ticket changes later: the scale is then only what it is
      // checked against.
      const weight = isDone
        ? pick(
            [patch.weightKg, manual("weightKg")],
            [route.weightKg, "saved"],
            [getRouteScaleWeight(route), "scale"],
          )
        : pick(
            [patch.weightKg, manual("weightKg")],
            [getRouteScaleWeight(route), "scale"],
            [route.weightKg, "saved"],
          );
      const mileage = isDone
        ? pick(
            [patch.mileage, manual("mileage")],
            [route.mileage || undefined, "saved"],
            [trackMileage, "track"],
          )
        : pick(
            [patch.mileage, manual("mileage")],
            [trackMileage, "track"],
            [route.mileage, "saved"],
          );
      const times = tripTimes.get(route.id);
      sources.set(route.id, {
        start: times?.startSource,
        end: times?.endSource,
        place: place.source,
        weight: weight.source,
        mileage: mileage.source,
      });
      // The unload place is approved into the route's own `destinationLocationId`,
      // the plan's field: GPS is all that is left to check it against.
      agreement.set(route.id, {
        start: times?.startAgrees,
        end: times?.endAgrees,
        place: agrees(place, sameId, [rawUnload?.locationId, "gps"]),
        weight: agrees(weight, closeTo(WEIGHT_EPSILON), [getRouteScaleWeight(route), "scale"]),
        mileage: agrees(mileage, closeTo(MILEAGE_EPSILON), [trackMileage, "track"]),
      });
      // The driver's marks become the approved times, so "actual" is one source.
      const actual: FieldSource = isDone ? "saved" : "driver";
      options.set(route.id, {
        start: optionsOf(
          [rawGps?.start, "gps"],
          [fromTimestamp(route.factStartAt), actual],
          [fromTimestamp(route.planStartAt), "plan"],
        ),
        end: optionsOf(
          [scaleTimeOf(route), "scale"],
          [rawUnload?.at, "gps"],
          [fromTimestamp(route.factEndAt), actual],
          [fromTimestamp(route.planEndAt), "plan"],
        ),
        place: optionsOf(
          [rawUnload?.locationId, "gps"],
          [route.destinationLocationId || undefined, isDone ? "saved" : "plan"],
        ),
        weight: optionsOf([getRouteScaleWeight(route), "scale"]),
        mileage: optionsOf([trackMileage, "track"]),
      });
      return {
        ...route,
        factStartAt: ts(patch.factStartAt) ?? route.factStartAt,
        factEndAt: ts(patch.factEndAt) ?? route.factEndAt,
        destinationLocationId: place.value ?? route.destinationLocationId,
        weightKg: weight.value,
        volume: getRouteFactVolume(route),
        mileage: mileage.value,
      } as ConnectProto.Route;
    });
    return {
      routesView: view,
      routeSources: sources,
      routeAgreement: agreement,
      routeOptions: options,
    };
  }, [routes, draft, gps.tripsGps, isDone, mileageByRoute, tripTimes]);

  const { shiftView, shiftSources, shiftAgreement, shiftOptions } = React.useMemo(() => {
    if (!shift || !shiftTimes)
      return {
        shiftView: undefined,
        shiftSources: undefined,
        shiftAgreement: undefined,
        shiftOptions: undefined,
      };
    const agrees = savedAgreement(isDone);
    const patch = draft.shift;
    const usersById = new Map([...shift.crew, ...crewCandidates].map((u) => [u.id, u] as const));
    const engineHours = pick(
      [patch.engineHours, patch.from?.engineHours ?? "manual"],
      [shift.engineHours, "saved"],
      [sensorEngineHours, "sensor"],
      [trackStats?.engineHours, "track"],
    );
    const mileage = isDone
      ? pick(
          [patch.mileage, patch.from?.mileage ?? "manual"],
          [shift.mileage || undefined, "saved"],
          [trackStats?.mileageKm, "track"],
        )
      : pick(
          [patch.mileage, patch.from?.mileage ?? "manual"],
          [trackStats?.mileageKm, "track"],
          [shift.mileage, "saved"],
        );
    const view = {
      ...shift,
      departureLocationId: shiftTimes.departureLocationId,
      arrivalLocationId: shiftTimes.arrivalLocationId,
      departureTime: ts(shiftTimes.departureTime),
      arrivalTime: ts(shiftTimes.arrivalTime),
      engineHours: engineHours.value,
      mileage: mileage.value,
      crew: draft.crewIds ? draft.crewIds.flatMap((id) => usersById.get(id) ?? []) : shift.crew,
    } as ConnectProto.DriverOnShift;
    const sources: ShiftFieldSources = {
      ...shiftTimes.sources,
      engineHours: engineHours.source,
      mileage: mileage.source,
    };
    const agreement: ShiftFieldAgreement = {
      ...shiftTimes.agreement,
      engineHours: agrees(
        engineHours,
        closeTo(ENGINE_HOURS_EPSILON),
        [sensorEngineHours, "sensor"],
        [trackStats?.engineHours, "track"],
      ),
      mileage: agrees(mileage, closeTo(MILEAGE_EPSILON), [trackStats?.mileageKm, "track"]),
    };
    const options: ShiftFieldOptions = {
      ...shiftTimes.options,
      engineHours: optionsOf([sensorEngineHours, "sensor"], [trackStats?.engineHours, "track"]),
      mileage: optionsOf([trackStats?.mileageKm, "track"]),
    };
    return {
      shiftView: view,
      shiftSources: sources,
      shiftAgreement: agreement,
      shiftOptions: options,
    };
  }, [shift, shiftTimes, draft, crewCandidates, sensorEngineHours, trackStats, isDone]);

  // Cancelled trips stay in the writes above (they get their notes as before)
  // but are left off the form, the jobs tab and the playback.
  const shownRoutes = React.useMemo(() => routesView.filter(isShownOnTtn), [routesView]);

  // The playback's trips carry the trip times as the TTN shows them — for an
  // approved TTN (`consignmentNoteDoneAt`) the approved value or an edit, for an
  // open one also GPS / scale / the driver's buttons — so its segments are the
  // TTN's even where the stored trip has no actual times yet. Plan times stay
  // out: a segment read as actual must not report the schedule.
  const playbackRoutes = React.useMemo(
    () =>
      shownRoutes.map((route) => {
        const times = tripTimes.get(route.id);
        const shown = (at: Date | undefined, source: FieldSource | undefined) =>
          source === "plan" ? undefined : ts(at);
        return {
          ...route,
          factStartAt: shown(times?.start, times?.startSource) ?? route.factStartAt,
          factEndAt: shown(times?.end, times?.endSource) ?? route.factEndAt,
        } as ConnectProto.Route;
      }),
    [shownRoutes, tripTimes],
  );

  // The TTN can only be closed once every real job is in a final status
  // (completed, force-completed, not completed or cancelled).
  const unfinishedJobsCount = routes
    .filter(isRealRoute)
    .flatMap((route) => route.jobs)
    .filter((job) => !job.isPseudo && !isFinalJobStatus(job.status)).length;

  // A departure after the return, and trips on the form whose start is after
  // their unload: they block approval.
  const isShiftTimeInverted = isTimeRangeInverted({
    start: shiftTimes?.departureTime,
    end: shiftTimes?.arrivalTime,
  });
  const invertedTripsCount = shownRoutes.filter((route) =>
    isTimeRangeInverted(tripTimes.get(route.id)),
  ).length;

  // Drops the shift's edits: the form shows the stored values again.
  const discard = () => {
    if (!shift) return;
    setDrafts((prev) => {
      const next = new Map(prev);
      next.delete(shift.id);
      return next;
    });
  };

  const writeTtn = useTtnWrites();
  /**
   * Resolves to whether the backend stamped the TTN done — only a complete one is.
   * `refresh` reloads the shift and its trips: the draft stays until they are in,
   * or the form would flash the pre-save values and offer them for download.
   */
  const save = async (refresh?: () => Promise<unknown>) => {
    if (!shift || !shiftView) return false;
    // On failure the draft is kept: nothing typed is lost and approve can be retried.
    const saved = await writeTtn({ shift, shiftView, routes, routesView, draft, tripTimes });
    // The TTN is written either way: a failed reload only means stale data on screen.
    await refresh?.().catch(() => undefined);
    discard();
    return isTtnApproved(saved);
  };

  return {
    shift: shiftView,
    /** The trips on the TTN — cancelled ones left out. */
    routes: shownRoutes,
    hasChanges:
      Object.keys(draft.shift).length > 0 || draft.crewIds !== undefined || draft.routes.size > 0,
    unfinishedJobsCount,
    invertedTripsCount,
    isShiftTimeInverted,
    setShift,
    setCrew,
    setRoute,
    save,
    discard,
    /** Each trip's start / unload as shown (the stored `fact*` stay on `routes`). */
    tripTimes,
    /** Where each shown value came from, for the fields' border colours. */
    sources: { shift: shiftSources, routes: routeSources },
    /**
     * For an approved TTN: which source each stored value still agrees with —
     * its border colour. Empty while the TTN is open, where the border already
     * says where the value is being read from.
     */
    agreement: { shift: shiftAgreement, routes: routeAgreement },
    /** What each field can be set to off its right-click menu, source by source. */
    options: { shift: shiftOptions, routes: routeOptions },
    /** The fields holding an edit, typed or picked — what "reset" can drop. */
    edits: { shift: draft.shift, routes: draft.routes },
    /** Mileage of the way home, last trip's end → shift return; read-only. */
    returnMileage,
    /** The trips for the playback: fact times are the TTN's shown ones. */
    playbackRoutes,
    /** Default window of the shift's track — also what the playback map shows. */
    trackWindow: shiftSpan,
  };
};
